import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Building2, Users, Trash2, Edit2, ShieldCheck, ArrowLeft } from 'lucide-react';

const MasterAdmin = () => {
  const navigate = useNavigate();
  const { isMasterAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Company dialog
  const [companyDialog, setCompanyDialog] = useState({ open: false, mode: 'create', data: null });
  const [cForm, setCForm] = useState({ name: '', description: '', is_active: true });

  // User dialog
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'create', data: null });
  const [uForm, setUForm] = useState({
    email: '', name: '', password: '', role: 'Submitter', department: '', company_ids: [], is_master_admin: false,
  });

  useEffect(() => {
    if (!isMasterAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, uRes] = await Promise.all([
        api.get('/companies'),
        api.get('/admin/users'),
      ]);
      setCompanies(cRes.data || []);
      setUsers(uRes.data || []);
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Companies ----------
  const openCreateCompany = () => {
    setCForm({ name: '', description: '', is_active: true });
    setCompanyDialog({ open: true, mode: 'create', data: null });
  };
  const openEditCompany = (c) => {
    setCForm({ name: c.name, description: c.description || '', is_active: c.is_active });
    setCompanyDialog({ open: true, mode: 'edit', data: c });
  };
  const saveCompany = async () => {
    try {
      if (companyDialog.mode === 'create') {
        await api.post('/companies', cForm);
        toast.success('Company created');
      } else {
        await api.put(`/companies/${companyDialog.data.id}`, cForm);
        toast.success('Company updated');
      }
      setCompanyDialog({ open: false, mode: 'create', data: null });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save company');
    }
  };
  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL of its data? This cannot be undone.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success('Company deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---------- Users ----------
  const openCreateUser = () => {
    setUForm({ email: '', name: '', password: '', role: 'Submitter', department: '', company_ids: [], is_master_admin: false });
    setUserDialog({ open: true, mode: 'create', data: null });
  };
  const openEditUser = (u) => {
    setUForm({
      email: u.email, name: u.name, password: '', role: u.role,
      department: u.department || '', company_ids: u.company_ids || [],
      is_master_admin: !!u.is_master_admin,
    });
    setUserDialog({ open: true, mode: 'edit', data: u });
  };
  const saveUser = async () => {
    try {
      if (userDialog.mode === 'create') {
        if (!uForm.password) { toast.error('Password is required'); return; }
        await api.post('/admin/users', uForm);
        toast.success('User created');
      } else {
        const payload = { ...uForm };
        if (!payload.password) delete payload.password;
        delete payload.email; // email change not supported here
        await api.put(`/admin/users/${userDialog.data.id}`, payload);
        toast.success('User updated');
      }
      setUserDialog({ open: false, mode: 'create', data: null });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save user');
    }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('User deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleUserCompany = (cid) => {
    setUForm(prev => {
      const has = prev.company_ids.includes(cid);
      return { ...prev, company_ids: has ? prev.company_ids.filter(x => x !== cid) : [...prev.company_ids, cid] };
    });
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || id;

  if (loading) {
    return <div className="p-12 text-center text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-[#26B8A7]" />
          <div>
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Master Admin</h1>
            <p className="text-sm text-slate-500">Manage companies, users and access</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/select-company')} data-testid="back-to-select-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to workspaces
        </Button>
      </div>

      <div className="p-8">
        <Tabs defaultValue="companies">
          <TabsList>
            <TabsTrigger value="companies" data-testid="ma-tab-companies"><Building2 className="w-4 h-4 mr-2" /> Companies ({companies.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="ma-tab-users"><Users className="w-4 h-4 mr-2" /> Users ({users.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Companies</CardTitle>
                <Button onClick={openCreateCompany} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="add-company-master-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add company
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companies.map(c => (
                    <div key={c.id} data-testid={`company-row-${c.id}`} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#235894] to-[#26B8A7] text-white flex items-center justify-center font-bold">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-slate-500">{c.description || '—'}</div>
                          <div className="text-xs text-slate-400 mt-1">{users.filter(u => (u.company_ids || []).includes(c.id)).length} users</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.is_active ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                        <Button variant="ghost" size="icon" onClick={() => openEditCompany(c)} data-testid={`edit-company-${c.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteCompany(c)} data-testid={`delete-company-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                  {companies.length === 0 && <p className="text-center text-slate-500 py-8">No companies yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Users</CardTitle>
                <Button onClick={openCreateUser} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="add-user-master-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add user
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.name}</span>
                          {u.is_master_admin && <Badge className="bg-amber-100 text-amber-700 border-0"><ShieldCheck className="w-3 h-3 mr-1" /> Master</Badge>}
                          <Badge variant="outline">{u.role}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{u.email}</div>
                        <div className="text-xs text-slate-400 mt-1">
                          Companies: {(u.company_ids || []).length === 0 ? '—' : (u.company_ids).map(companyName).join(', ')}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Company Dialog */}
      <Dialog open={companyDialog.open} onOpenChange={(open) => setCompanyDialog({ ...companyDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{companyDialog.mode === 'create' ? 'Add company' : 'Edit company'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} data-testid="company-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} data-testid="company-description-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={cForm.is_active} onCheckedChange={(v) => setCForm({ ...cForm, is_active: !!v })} id="active" />
              <Label htmlFor="active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCompanyDialog({ ...companyDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCompany} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="save-company-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ ...userDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{userDialog.mode === 'create' ? 'Add user' : 'Edit user'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={uForm.email} disabled={userDialog.mode === 'edit'} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email-input" />
              </div>
            </div>
            <div>
              <Label>{userDialog.mode === 'edit' ? 'New Password (leave blank to keep)' : 'Password'}</Label>
              <Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Submitter">Submitter</SelectItem>
                    <SelectItem value="Approver">Approver</SelectItem>
                    <SelectItem value="CI Manager">CI Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Input value={uForm.department} onChange={(e) => setUForm({ ...uForm, department: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={uForm.is_master_admin} onCheckedChange={(v) => setUForm({ ...uForm, is_master_admin: !!v })} id="is_master" data-testid="user-master-admin-checkbox" />
              <Label htmlFor="is_master">Master admin (full platform access)</Label>
            </div>
            <div>
              <Label className="mb-2 block">Company access</Label>
              <div className="space-y-2 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                {companies.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <Checkbox
                      checked={uForm.company_ids.includes(c.id)}
                      onCheckedChange={() => toggleUserCompany(c.id)}
                      data-testid={`user-company-${c.id}`}
                    />
                    <span className="text-sm">{c.name}</span>
                  </label>
                ))}
                {companies.length === 0 && <p className="text-xs text-slate-400">No companies yet</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUserDialog({ ...userDialog, open: false })}>Cancel</Button>
            <Button onClick={saveUser} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterAdmin;
